import { getOwner } from "@ember/application"
import { action, computed, get } from "@ember/object"
import type ObjectProxy from "@ember/object/proxy"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { RotationCalendarRotation, RotationType } from "@shared/models/rotation-calendar"
import {
  CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS,
  NEW_PLANBOOK_DEFAULT_SCHOOL_YEAR_OPTION,
} from "@shared/models/school-year"
import type { User } from "@shared/models/user"
import ObjectId from "@shared/util/object-id-gen"
import findDocument from "cc-frontend/helpers/find-document"
import findMany from "cc-frontend/helpers/find-many"
import userUpdateAccount from "cc-frontend/lib/actions/user/USER_UPDATE_ACCOUNT"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { isCachedMembershipActiveInclAncestors } from "cc-frontend/utils/group"
import { generatePlanbookCalendar } from "cc-frontend/utils/planbook/generate-calendar"
import { convertHolidayToPlanbookDateAnnotation } from "cc-frontend/utils/planbook/holiday"
import { createRotationCalendarAttributes } from "cc-frontend/utils/rotation-calendar/create-attributes"
import _ from "lodash"
import { set as _set, find, first, map, pull, reduce, toArray } from "lodash-es"
import { tracked, TrackedArray, TrackedObject } from "tracked-built-ins"
import styles from "./create-planbook-form.module.css"

interface Signature {
  Args: {
    userId: string
  }
}

export default class CreatePlanbookForm extends Component<Signature> {
  readonly styles = styles

  @service declare dispatcher: Dispatcher

  @service("ember-store") declare store: EmberStore
  @service declare modalManager: ModalManager
  @service declare rpc: Rpc
  @service declare session: Session

  readonly createPlanbookSchoolYearOptions = CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS

  @tracked newPlanbookYearOptionId = NEW_PLANBOOK_DEFAULT_SCHOOL_YEAR_OPTION.id
  @tracked rotationType: RotationType = "weekly"
  @tracked weekDays: "SU_TH" | "MO_FR" | "TU_SA" = "MO_FR"

  //@ts-ignore
  @tracked blockRotations: Array<RotationCalendarRotation> = new TrackedArray([
    new TrackedObject({ abbreviation: "A", id: ObjectId.create(), position: 1000, title: "A Day" }),
    new TrackedObject({ abbreviation: "B", id: ObjectId.create(), position: 2000, title: "B Day" }),
  ])
  /**
   * Keep track of what was added so if someone adds a rotation, then deletes the A day
   * and then adds another day, it won't be C (which is what it would be if we just use
   * the index of the block rotations)
   */
  @tracked addedRotationCount = 2
  @tracked planbookTitle = "My Awesome Planbook"
  @tracked customizedGroupRoles = tracked({})
  @tracked isCreatingPlanbook = false
  @tracked availableRoles = [
    { id: "planbook:is-commenter", title: "Can Comment" },
    { id: "planbook:is-editor", title: "Can Edit" },
  ]

  @findDocument("args.userId", "user") declare user: ObjectProxy<User>
  @findMany("schoolAndNetworkGroupIds", "group") schoolsAndNetworks

  @computed("user.content.attributes.cachedMemberships.[]")
  get schoolAndNetworkGroupIds() {
    if (!this.user.content) return []

    return pipe(
      this.user.content.attributes.cachedMemberships,
      A.filter(isCachedMembershipActiveInclAncestors),
      A.filter(
        (membership) => membership.usedAs === "school" || membership.usedAs === "school-network"
      ),
      A.map((membership) => membership.groupId),
      A.uniq
    )
  }

  get groupRoles() {
    return reduce(
      this.schoolAndNetworkGroupIds,
      (acc, id) => {
        return _set(acc, id, this.customizedGroupRoles[id] || "planbook:is-editor")
      },
      {}
    )
  }

  @action async createPlanbook(event) {
    event.preventDefault()
    if (this.isCreatingPlanbook === true) return
    this.isCreatingPlanbook = true
    const { schoolYearId, startDate, endDate, semesterTitle } =
      this.createPlanbookSchoolYearOptions.find(
        (option) => option.id === this.newPlanbookYearOptionId
      )!
    let collaborators = _.map(this.groupRoles, (val, key) => {
      return {
        groupId: key,
        role: val,
      }
    })
    try {
      var { data: holidays } = await this.rpc.holidays_for_school_year({ schoolYearId })
    } catch (error) {
      console.error(error)
    }

    let semester = {
      startDate: startDate,
      endDate: endDate,
      title: semesterTitle,
      id: ObjectId.create(),
    }

    let schoolDays
    if (this.weekDays === "SU_TH") {
      schoolDays = [0, 1, 2, 3, 4]
    }

    if (this.weekDays === "MO_FR") {
      schoolDays = [1, 2, 3, 4, 5]
    }

    if (this.weekDays === "TU_SA") {
      schoolDays = [2, 3, 4, 5, 6]
    }

    let rotationCalendarAttributes = createRotationCalendarAttributes({
      rotationType: this.rotationType,
      semester,
      schoolYearId,
      rotations: this.blockRotations,
      schoolDays: schoolDays,
    })

    let dateAnnotations = map(holidays, (holiday) =>
      convertHolidayToPlanbookDateAnnotation(holiday)
    )

    let planbookCalendar = generatePlanbookCalendar({ rotationCalendarAttributes, dateAnnotations })

    this.rpc
      .planbook_create_v2({
        title: this.planbookTitle,
        schoolYearId: schoolYearId,
        // @ts-ignore Fix the typings for this RPC function. Needs to improved on the elixir side
        planbookCalendar: planbookCalendar,
        // @ts-ignore
        rotationCalendarAttributes: rotationCalendarAttributes,
        ownerId: this.args.userId,
        collaborators: collaborators,
      })
      .then((res) => {
        if (res === undefined || res.responseOk === false) {
          this.isCreatingPlanbook = false
        } else {
          get(this, "modalManager").closeModal("user/create-planbook")
          this.dispatcher.dispatchPreparedAction(
            userUpdateAccount({
              field: "onboardingInProgress",
              value: false,
              userId: this.args.userId,
            })
          )
          let router = getOwner(this)!.lookup("router:main") as any
          if (router.currentRouteName === "user.sign-up") {
            // We want to direct to the user show so the user presses the back button
            // to go to the home instead of going back to the user sign up steps
            router.replaceWith("user.show", this.args.userId, {
              queryParams: { redirectToPlanbookId: res.result.id },
            })
          } else {
            router.transitionTo("planbookv5.planner.week", res.result.id)
          }
        }
      })
  }

  @action setGroupRole(groupId, roleMap) {
    this.customizedGroupRoles[groupId] = roleMap.id
  }

  @action removeRotation(rotation) {
    pull(this.blockRotations, rotation)
  }

  @action addRotation() {
    this.addedRotationCount++
    let dayName = [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
    ][this.addedRotationCount - 1]
    this.blockRotations.push(
      new TrackedObject({
        abbreviation: dayName,
        title: `${dayName} Day`,
        position: this.addedRotationCount * 1000,
        id: ObjectId.create(),
      })
    )
  }

  @action setRotationTitle(rotation, event) {
    let blockRotation = find(this.blockRotations, { id: rotation.id })
    if (blockRotation === undefined) return
    blockRotation.title = event.target.value || ""
    // the lodash.toArray respects emoji and doesn't split them.
    blockRotation.abbreviation = first(toArray(event.target.value || " "))
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Users::CreatePlanbookForm": typeof CreatePlanbookForm
  }
}
